// dither-maps.js — palette, ordered-dither threshold maps, glyph rasterisation
// and reveal-delay noise for the boot splash voxel stage (dither3d.js).
// Zero dependencies. Everything here is pure data / pure math so the engine
// stays focused on projection + drawing.

export const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

export function smoothstep(e0, e1, x) {
  const t = clamp((x - e0) / (e1 - e0 || 1e-6), 0, 1);
  return t * t * (3 - 2 * t);
}

export function mulberry32(seed) {
  let s = seed >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ---------------------------------------------------------------- palette ---
// Straight from the aquaOS theme: grey-green bevels (#1a201f -> #8b9694) and
// the gold starburst accent (#d9c34a / #ffd85a) on a near-black desk.

export const BG = '#0c0f0e';

export const RAMPS = {
  grey: ['#141a19', '#1f2726', '#2f3937', '#42504d', '#5b6a67', '#788480', '#8b9694'],
  gold: ['#231c08', '#3d3210', '#66541a', '#96802a', '#c0a63c', '#d9c34a', '#ffd85a'],
};

export const RAMP_IDS = ['grey', 'gold'];

function hexToRgb(hex) {
  const n = parseInt(hex.slice(1), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export function mixHex(a, b, t) {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  const c = (i) => Math.round(A[i] + (B[i] - A[i]) * t);
  return `rgb(${c(0)},${c(1)},${c(2)})`;
}

// COLORS[rampIndex][fogIndex][level] — ramp levels pre-mixed toward the
// background so distant cells fall away into the dark without per-frame maths.
export function buildColorTable(fogSteps, fogMax) {
  return RAMP_IDS.map((id) => {
    const ramp = RAMPS[id];
    const byFog = [];
    for (let f = 0; f < fogSteps; f++) {
      const t = (f / (fogSteps - 1)) * fogMax;
      byFog.push(ramp.map((hex) => mixHex(hex, BG, t)));
    }
    return byFog;
  });
}

// ------------------------------------------------------- threshold maps ---

export const THRESHOLD_MAPS = [
  { id: 'bayer4', label: 'Bayer 4x4' },
  { id: 'bayer8', label: 'Bayer 8x8' },
  { id: 'voidcluster', label: 'Void & Cluster' },
];

const cache = new Map();

export function getThresholdMap(id) {
  const key = THRESHOLD_MAPS.some((m) => m.id === id) ? id : 'bayer8';
  if (!cache.has(key)) {
    if (key === 'bayer4') cache.set(key, bayer(4));
    else if (key === 'bayer8') cache.set(key, bayer(8));
    else cache.set(key, voidAndCluster(16));
  }
  return cache.get(key);
}

// Classic recursive Bayer construction, normalised to (rank + 0.5) / n^2.
function bayer(n) {
  let m = [[0]];
  let s = 1;
  while (s < n) {
    const next = [];
    for (let y = 0; y < s * 2; y++) next.push(new Array(s * 2).fill(0));
    for (let y = 0; y < s; y++) {
      for (let x = 0; x < s; x++) {
        const v = m[y][x] * 4;
        next[y * 2][x * 2] = v;
        next[y * 2][x * 2 + 1] = v + 2;
        next[y * 2 + 1][x * 2] = v + 3;
        next[y * 2 + 1][x * 2 + 1] = v + 1;
      }
    }
    m = next;
    s *= 2;
  }
  const data = new Float32Array(n * n);
  for (let y = 0; y < n; y++) {
    for (let x = 0; x < n; x++) data[y * n + x] = (m[y][x] + 0.5) / (n * n);
  }
  return { size: n, data };
}

// Ulichney's void-and-cluster: a blue-noise threshold matrix built by
// repeatedly relocating the tightest cluster into the largest void, then
// ranking every pixel. Toroidal gaussian energy field, kept incremental.
function voidAndCluster(n) {
  const N = n * n;
  const rng = mulberry32(0x5eed1234);
  const bin = new Uint8Array(N);
  const energy = new Float32Array(N);
  const kernel = new Float32Array(N);
  const sig2 = 2 * 1.5 * 1.5;
  for (let dy = 0; dy < n; dy++) {
    for (let dx = 0; dx < n; dx++) {
      const ox = Math.min(dx, n - dx);
      const oy = Math.min(dy, n - dy);
      kernel[dy * n + dx] = Math.exp(-(ox * ox + oy * oy) / sig2);
    }
  }
  const bump = (idx, sign) => {
    const px = idx % n;
    const py = (idx / n) | 0;
    for (let y = 0; y < n; y++) {
      const dy = (y - py + n) % n;
      for (let x = 0; x < n; x++) {
        const dx = (x - px + n) % n;
        energy[y * n + x] += sign * kernel[dy * n + dx];
      }
    }
  };
  const tightest = () => {
    let best = -1;
    let bv = -Infinity;
    for (let i = 0; i < N; i++) if (bin[i] && energy[i] > bv) ((bv = energy[i]), (best = i));
    return best;
  };
  const largestVoid = () => {
    let best = -1;
    let bv = Infinity;
    for (let i = 0; i < N; i++) if (!bin[i] && energy[i] < bv) ((bv = energy[i]), (best = i));
    return best;
  };

  const ones = Math.max(1, Math.round(N * 0.1));
  for (let k = 0; k < ones;) {
    const i = (rng() * N) | 0;
    if (!bin[i]) {
      bin[i] = 1;
      bump(i, 1);
      k++;
    }
  }
  // phase 0 — homogenise the seed pattern
  for (let it = 0; it < N; it++) {
    const c = tightest();
    bin[c] = 0;
    bump(c, -1);
    const v = largestVoid();
    bin[v] = 1;
    bump(v, 1);
    if (v === c) break;
  }

  const proto = bin.slice();
  const rank = new Int32Array(N);
  // phase 1 — rank the seed ones downward by removing tightest clusters
  for (let r = ones - 1; r >= 0; r--) {
    const c = tightest();
    bin[c] = 0;
    bump(c, -1);
    rank[c] = r;
  }
  // restore the seed pattern
  for (let i = 0; i < N; i++) {
    if (proto[i]) {
      bin[i] = 1;
      bump(i, 1);
    }
  }
  // phase 2 — rank the remaining zeros upward by filling largest voids
  for (let r = ones; r < N; r++) {
    const v = largestVoid();
    bin[v] = 1;
    bump(v, 1);
    rank[v] = r;
  }

  const data = new Float32Array(N);
  for (let i = 0; i < N; i++) data[i] = (rank[i] + 0.5) / N;
  return { size: n, data };
}

// ------------------------------------------------------------ source mask ---

function boxBlur(src, w, h, r) {
  const tmp = new Float32Array(w * h);
  const out = new Float32Array(w * h);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let s = 0;
      let n = 0;
      for (let k = -r; k <= r; k++) {
        const xx = x + k;
        if (xx < 0 || xx >= w) continue;
        s += src[y * w + xx];
        n++;
      }
      tmp[y * w + x] = s / n;
    }
  }
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      let s = 0;
      let n = 0;
      for (let k = -r; k <= r; k++) {
        const yy = y + k;
        if (yy < 0 || yy >= h) continue;
        s += tmp[yy * w + x];
        n++;
      }
      out[y * w + x] = s / n;
    }
  }
  return out;
}

// Rasterise the caller's glyph offscreen at grid resolution (Canvas2D
// antialiasing gives us free edge coverage), then compose the per-cell target
// luminance: solid glyph + a blurred halo + a gentle radial field so the
// background still carries dither texture.
export function buildMask(opts = {}) {
  const { path, cols, rows } = opts;
  const vb = normaliseViewBox(opts.viewBox);
  const fill = opts.fill ?? 0.6;
  const glow = opts.glow ?? 0.52;
  const ambient = opts.ambient ?? 0.2;
  const cov = new Float32Array(cols * rows);

  if (path && typeof document !== 'undefined') {
    const off = document.createElement('canvas');
    off.width = cols;
    off.height = rows;
    const g = off.getContext('2d', { willReadFrequently: true });
    const s = Math.min((cols * fill) / vb[2], (rows * fill) / vb[3]);
    g.translate(cols / 2 - (vb[0] + vb[2] / 2) * s, rows / 2 - (vb[1] + vb[3] / 2) * s);
    g.scale(s, s);
    g.fillStyle = '#fff';
    g.fill(new Path2D(path));
    const px = g.getImageData(0, 0, cols, rows).data;
    for (let i = 0; i < cols * rows; i++) cov[i] = px[i * 4 + 3] / 255;
  }

  const halo = boxBlur(boxBlur(cov, cols, rows, 2), cols, rows, 3);
  const luma = new Float32Array(cols * rows);
  const cx = (cols - 1) / 2;
  const cy = (rows - 1) / 2;
  const maxR = Math.hypot(cx, cy) || 1;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const i = r * cols + c;
      const rad = 1 - Math.hypot(c - cx, r - cy) / maxR;
      const field = ambient * Math.pow(Math.max(0, rad), 1.6);
      const rest = 1 - cov[i];
      luma[i] = clamp(cov[i] + (glow * halo[i] + field) * rest, 0, 1);
    }
  }
  return { luma, cov };
}

function normaliseViewBox(vb) {
  if (Array.isArray(vb) && vb.length === 4) return vb.map(Number);
  if (typeof vb === 'string') {
    const p = vb
      .trim()
      .split(/[\s,]+/)
      .map(Number);
    if (p.length === 4) return p;
  }
  return [0, 0, 384, 512];
}

// -------------------------------------------------------- reveal delays ---
// Smooth value noise on a coarse lattice (cells pop in scattered blobs) with a
// radial bias, so the image materialises from the middle outward.
export function buildDelays(cols, rows, seed = 0x51ed) {
  const rng = mulberry32(seed);
  const cell = 8;
  const lw = Math.ceil(cols / cell) + 2;
  const lh = Math.ceil(rows / cell) + 2;
  const lat = new Float32Array(lw * lh);
  for (let i = 0; i < lat.length; i++) lat[i] = rng();

  const out = new Float32Array(cols * rows);
  const cx = (cols - 1) / 2;
  const cy = (rows - 1) / 2;
  const maxR = Math.hypot(cx, cy) || 1;
  const fade = (t) => t * t * (3 - 2 * t);
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const fx = c / cell;
      const fy = r / cell;
      const x0 = Math.floor(fx);
      const y0 = Math.floor(fy);
      const tx = fade(fx - x0);
      const ty = fade(fy - y0);
      const a = lat[y0 * lw + x0];
      const b = lat[y0 * lw + x0 + 1];
      const d = lat[(y0 + 1) * lw + x0];
      const e = lat[(y0 + 1) * lw + x0 + 1];
      const top = a + (b - a) * tx;
      const bot = d + (e - d) * tx;
      const n = top + (bot - top) * ty;
      const rad = Math.hypot(c - cx, r - cy) / maxR;
      out[r * cols + c] = clamp(0.06 + 0.4 * n + 0.32 * rad, 0, 0.8);
    }
  }
  return out;
}
